import type {
  CreateOrUpdateProductRequest,
  ProductOption,
  ProductResponse as GeneratedProductResponse,
  ProductResponseOption,
  UpdateProductOptionRequest,
} from "@przezmorze/api-types";
import { przezmorzeApi, throwMessageOnBadRequestStatus } from "../../../api/api";
import type { PriceValue } from "../../misc/price";
import type { ProviderProductType } from "../providers/providersApi";
import type { PriceType } from "./priceType";

export type { CreateOrUpdateProductRequest } from "@przezmorze/api-types";
export type { ProductResponseOption as ProductOptionResponse } from "@przezmorze/api-types";

// See providersApi.ts's ProviderProductType for why productType keeps a real TS enum here instead of the
// generated plain string union.
export type ProductResponse = Omit<GeneratedProductResponse, "provider"> & {
  readonly provider: Omit<GeneratedProductResponse["provider"], "productType"> & {
    readonly productType?: ProviderProductType;
  };
};

// providerDetails and priceMargin are the only fields ProductResponseOption trims per-role rather than
// per-data: the backend mapper always populates both (providerDetails as an object, priceMargin as an object
// or null) for ADMIN/AGENT, and always omits both for CUSTOMER — see products.controller.ts's
// productToProductResponse. Every admin/products page is reachable only by ADMIN/AGENT (route-gated), so an
// option arriving there is guaranteed to have both fields; this narrowing lets the admin option-editing forms
// treat them as always-present instead of threading `?? {}`/`?? null` fallbacks through every call site.
export type AdminProductOptionResponse = Omit<ProductResponseOption, "providerDetails" | "priceMargin"> &
  Required<Pick<ProductResponseOption, "providerDetails" | "priceMargin">>;

export type AdminProductResponse = Omit<ProductResponse, "options"> & {
  readonly options: AdminProductOptionResponse[];
};

type PriceListPriceValue = Exclude<PriceValue, "INVALID">;

interface CreateOptionRequest {
  readonly name: string;
}

export interface OptionPricesDTO {
  readonly selling: Record<string, PriceListPriceValue>;
  readonly purchase: Record<string, PriceListPriceValue>;
  readonly fit: Record<string, PriceListPriceValue>;
}

export interface OptionPricesResponse {
  readonly optionId: string;
  readonly productId: string;
  readonly prices: OptionPricesDTO;
  readonly usePricesFrom?: { readonly optionId: string; readonly productId: string };
  readonly useDynamicPrices: boolean;
  readonly computePricesFromPurchasePrice: boolean;
}

interface DateRange {
  readonly start: string;
  readonly end: string;
}

export type UpdatePricesBatchRequest = {
  options: { optionId: string; value: PriceListPriceValue }[];
} & (
  | { type: "EVERY_OTHER_DAY"; dateRanges: DateRange[] }
  | { type: "DAYS_OF_WEEK"; ranges: { dates: DateRange; daysOfWeek: number[] }[] }
);

export interface UpdateOptionSellingPriceResponse {
  readonly optionId: string;
  readonly productId: string;
  readonly prices: { readonly selling: Record<string, number> };
}

export interface UpdateOptionPurchasePriceResponse {
  readonly optionId: string;
  readonly productId: string;
  readonly prices: { readonly purchase: Record<string, number> };
}

export type ConvertPricesRequest = {
  options: string[];
  mode: "MULTIPLIER" | "AMOUNT";
  source: "fit" | "purchase" | "selling";
  targets: { priceType: "fit" | "purchase" | "selling"; value: number }[];
} & (
  | { type: "EVERY_OTHER_DAY"; dateRanges: DateRange[] }
  | { type: "DAYS_OF_WEEK"; ranges: { dates: DateRange; daysOfWeek: number[] }[] }
);

export interface LinkPricesResponse {
  readonly optionId: string;
  readonly productId: string;
  readonly pricesFrom: { readonly optionId: string; readonly productId: string };
}

export type OptionPriceValue = number | "UNAVAILABLE" | "ON_DEMAND";

export type ProductPricesResponse = Record<
  string,
  Record<string, { readonly price: OptionPriceValue; readonly availableQuantity?: number }>
>;

export async function listProducts(abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.get<{ items: ProductResponse[] }>("/products", {
    signal: abortSignal,
  });
  return response.data.items;
}

export async function getProduct(productId: string, abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.get<ProductResponse>(`/products/${productId}`, { signal: abortSignal });
  return response.data;
}

export async function createProduct(request: CreateOrUpdateProductRequest, abortSignal?: AbortSignal) {
  try {
    const response = await przezmorzeApi.post<ProductResponse>("/products", request, { signal: abortSignal });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function cloneProduct(productId: string, abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.post<ProductResponse>(
    `/products/${productId}/clone`,
    {},
    { signal: abortSignal },
  );
  return response.data;
}

export async function updateProduct(
  productId: string,
  request: CreateOrUpdateProductRequest,
  abortSignal?: AbortSignal,
) {
  try {
    const response = await przezmorzeApi.put<ProductResponse>(`/products/${productId}`, request, {
      signal: abortSignal,
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function deleteProduct(productId: string, abortSignal?: AbortSignal) {
  await przezmorzeApi.delete(`/products/${productId}`, { signal: abortSignal });
}

export async function createProductOption(productId: string, request: CreateOptionRequest, abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.post<{ id: string; name: string }>(`/products/${productId}/options`, request, {
    signal: abortSignal,
  });
  return response.data;
}

export async function deleteProductOption(
  productId: string,
  optionId: string,
  abortSignal?: AbortSignal,
): Promise<void> {
  await przezmorzeApi.delete(`/products/${productId}/options/${optionId}`, {
    signal: abortSignal,
  });
}

export async function reorderProductOptions(
  productId: string,
  optionIds: string[],
  abortSignal?: AbortSignal,
): Promise<void> {
  await przezmorzeApi.put(`/products/${productId}/options/reorder`, { optionIds }, { signal: abortSignal });
}

export async function updateProductOption(
  productId: string,
  optionId: string,
  request: UpdateProductOptionRequest,
  abortSignal?: AbortSignal,
) {
  const response = await przezmorzeApi.put<ProductOption>(`/products/${productId}/options/${optionId}`, request, {
    signal: abortSignal,
  });
  return response.data;
}

export async function getOptionPrices(productId: string, optionId: string, abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.get<OptionPricesResponse>(`/products/${productId}/options/${optionId}/prices`, {
    signal: abortSignal,
  });
  return response.data;
}

export async function updateOptionPrices(
  productId: string,
  optionId: string,
  request: OptionPricesDTO,
  abortSignal?: AbortSignal,
) {
  const response = await przezmorzeApi.put<OptionPricesResponse>(
    `/products/${productId}/options/${optionId}/prices`,
    request,
    {
      signal: abortSignal,
    },
  );
  return response.data;
}

export async function updatePricesBatch(
  productId: string,
  priceType: PriceType,
  request: UpdatePricesBatchRequest,
  abortSignal?: AbortSignal,
) {
  try {
    const response = await przezmorzeApi.put<{ productId: string; optionIds: string[] }>(
      `/products/${productId}/prices/${priceType}`,
      request,
      {
        signal: abortSignal,
      },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updateOptionSellingPriceForDate(
  productId: string,
  optionId: string,
  date: string,
  request: { value: PriceListPriceValue },
  abortSignal?: AbortSignal,
) {
  const response = await przezmorzeApi.put<UpdateOptionSellingPriceResponse>(
    `/products/${productId}/options/${optionId}/prices/selling/${date}`,
    request,
    { signal: abortSignal },
  );
  return response.data;
}

export async function updateOptionPurchasePriceForDate(
  productId: string,
  optionId: string,
  date: string,
  request: { value: PriceListPriceValue },
  abortSignal?: AbortSignal,
) {
  const response = await przezmorzeApi.put<UpdateOptionPurchasePriceResponse>(
    `/products/${productId}/options/${optionId}/prices/purchase/${date}`,
    request,
    { signal: abortSignal },
  );
  return response.data;
}

export async function convertPrices(productId: string, request: ConvertPricesRequest, abortSignal?: AbortSignal) {
  try {
    const response = await przezmorzeApi.put<{ productId: string; optionIds: string[] }>(
      `/products/${productId}/convert-prices`,
      request,
      {
        signal: abortSignal,
      },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function getProductPricesPdf(
  productId: string,
  priceType: PriceType,
  year: number,
  abortSignal?: AbortSignal,
) {
  const response = await przezmorzeApi.get<Blob>(`/products/${productId}/prices/${priceType}/${year}/pdf`, {
    signal: abortSignal,
    headers: { Accept: "application/pdf" },
    responseType: "blob",
  });
  return response.data;
}

export async function linkOptionPrices(
  productId: string,
  optionId: string,
  assignFrom: { productId: string; optionId: string },
  abortSignal?: AbortSignal,
): Promise<LinkPricesResponse> {
  const response = await przezmorzeApi.put<LinkPricesResponse>(
    `/products/${productId}/options/${optionId}/linked-prices`,
    { assignFrom },
    {
      signal: abortSignal,
    },
  );
  return response.data;
}

export async function unlinkOptionPrices(
  productId: string,
  optionId: string,
  copyPrices: boolean,
  abortSignal?: AbortSignal,
): Promise<void> {
  await przezmorzeApi.delete(`/products/${productId}/options/${optionId}/linked-prices`, {
    signal: abortSignal,
    params: { copyPrices },
  });
}

export async function getProductSellingPrices(
  productId: string | undefined | null,
  params: { readonly dateFrom: string; readonly dateTo: string; readonly participantsNumber: number },
  abortSignal?: AbortSignal,
): Promise<ProductPricesResponse> {
  if (!productId || !params.dateFrom || !params.dateTo || !params.participantsNumber) {
    return {};
  }
  const response = await przezmorzeApi.get<ProductPricesResponse>(`/products/${productId}/prices/selling`, {
    params,
    signal: abortSignal,
  });
  return response.data;
}

export async function pricesOnDate(date: string, abortSignal?: AbortSignal) {
  if (!date) {
    return {};
  }
  const response = await przezmorzeApi.get<Record<string, number>>(`/prices-on-date/${date}`, {
    signal: abortSignal,
  });
  return response.data;
}
